import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";

import { cn } from "@/lib/cn";

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitive.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(function ToastViewport({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      className={cn(
        "fixed right-0 bottom-0 z-(--z-toast) m-0 flex w-full max-w-[420px] list-none flex-col gap-2 p-4 outline-none",
        "pb-[max(1rem,env(safe-area-inset-bottom))]",
        className,
      )}
      {...props}
    />
  );
});

const toastVariants = cva(
  [
    "anim-rise-in anim-fade-out pointer-events-auto relative flex w-full items-start gap-3 rounded-card border p-4 shadow-float",
    "data-[swipe=end]:opacity-0 data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)",
  ],
  {
    variants: {
      tone: {
        default: "border-line bg-surface text-ink",
        success: "border-accent-line bg-accent-soft text-accent-text-on-soft",
        warn: "border-warn-line bg-warn-bg text-warn",
        danger: "border-danger-line bg-danger-bg text-danger",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export type ToastTone = NonNullable<VariantProps<typeof toastVariants>["tone"]>;

export const Toast = forwardRef<
  ElementRef<typeof ToastPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & VariantProps<typeof toastVariants>
>(function Toast({ className, tone, ...props }, ref) {
  return (
    <ToastPrimitive.Root ref={ref} className={cn(toastVariants({ tone }), className)} {...props} />
  );
});

export const ToastTitle = forwardRef<
  ElementRef<typeof ToastPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(function ToastTitle({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Title ref={ref} className={cn("text-base font-medium", className)} {...props} />
  );
});

export const ToastDescription = forwardRef<
  ElementRef<typeof ToastPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(function ToastDescription({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cn("text-sm opacity-90", className)}
      {...props}
    />
  );
});

export const ToastAction = forwardRef<
  ElementRef<typeof ToastPrimitive.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(function ToastAction({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Action
      ref={ref}
      className={cn(
        "inline-flex h-11 shrink-0 items-center rounded-control border border-current/30 px-3 text-sm font-medium hover:bg-current/10",
        className,
      )}
      {...props}
    />
  );
});

export const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(function ToastClose({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Close
      ref={ref}
      aria-label="Dismiss"
      className={cn(
        "inline-flex size-11 shrink-0 items-center justify-center rounded-control opacity-70 hover:opacity-100",
        className,
      )}
      {...props}
    >
      <X className="size-5" aria-hidden="true" />
    </ToastPrimitive.Close>
  );
});
